import React, { useState, useEffect, useCallback } from 'react';
import PropTypes from 'prop-types';
import { Label } from 'reactstrap';
import * as Yup from 'yup';
import { useFormik } from 'formik';

import AppButton from 'components/App/Button';
import Authentication from 'components/App/Authentication';
import Meta from 'components/App/Meta';
import images from 'assets/images';
import { LOGIN_TYPE } from 'constants/index';

import styles from './ForgotPassword.module.scss';

const OTP_FIELDS = ['otp-0', 'otp-1', 'otp-2', 'otp-3', 'otp-4', 'otp-5'];

const ConfirmPassword = (props) => {
  const {
    loginType,
    forgotPasswordLoading,
    onSubmitOtp,
    onResendOtp,
  } = props;

  const [otp, setOtp] = useState(new Array(6).fill(''));
  const [seconds, setSeconds] = useState(30);

  const validationStep2 = useFormik({
    initialValues: {
      code: '',
    },
    validationSchema: Yup.object({
      code: Yup.string().length(6, 'Mã OTP phải có 6 ký tự').required('Vui lòng nhập mã OTP'),
    }),
    onSubmit: (values) => {
      onSubmitOtp(values.code);
    },
  });

  useEffect(() => {
    let myInterval;
    if (seconds > 0) {
      myInterval = setInterval(() => {
        setSeconds((prev) => prev - 1);
      }, 1000);
    }
    return () => {
      if (myInterval) clearInterval(myInterval);
    };
  }, [seconds]);

  const handleResendOtp = useCallback(() => {
    if (onResendOtp) {
      onResendOtp();
    }
    setSeconds(30);
    setOtp(new Array(6).fill(''));
    validationStep2.setFieldValue('code', '');
    setTimeout(() => {
      document.getElementById('otp-0')?.focus();
    }, 50);
  }, [validationStep2, onResendOtp]);

  const handleOtpChange = useCallback((e, index) => {
    const { value } = e.target;
    if (value && !/^\d+$/.test(value)) return;

    const newOtp = [...otp];
    newOtp[index] = value.substring(value.length - 1);
    setOtp(newOtp);

    validationStep2.setFieldValue('code', newOtp.join(''));

    // Move focus to next input if value is entered
    if (value && index < 5) {
      const nextInput = document.getElementById(`otp-${index + 1}`);
      if (nextInput) {
        nextInput.focus();
      }
    }
  }, [otp, validationStep2]);

  const handleOtpKeyDown = useCallback((e, index) => {
    if (e.key === 'Backspace') {
      const newOtp = [...otp];
      if (!newOtp[index] && index > 0) {
        newOtp[index - 1] = '';
        setOtp(newOtp);
        validationStep2.setFieldValue('code', newOtp.join(''));
        const prevInput = document.getElementById(`otp-${index - 1}`);
        if (prevInput) {
          prevInput.focus();
        }
      } else {
        newOtp[index] = '';
        setOtp(newOtp);
        validationStep2.setFieldValue('code', newOtp.join(''));
      }
    }
  }, [otp, validationStep2]);

  const handlePaste = useCallback((e) => {
    e.preventDefault();
    const pastedData = e.clipboardData.getData('text').trim();
    if (!/^\d+$/.test(pastedData)) return;

    const digits = pastedData.substring(0, 6).split('');
    const newOtp = new Array(6).fill('').map((_, i) => digits[i] || '');

    setOtp(newOtp);
    validationStep2.setFieldValue('code', newOtp.join(''));

    const focusIndex = Math.min(digits.length, 5);
    const targetInput = document.getElementById(`otp-${focusIndex}`);
    if (targetInput) {
      targetInput.focus();
    }
  }, [validationStep2]);

  const renderOtpForm = () => (
    <form onSubmit={(e) => { e.preventDefault(); validationStep2.handleSubmit(); }}>
      <div className={styles.OtpContainer}>
        {OTP_FIELDS.map((fieldId, index) => (
          <input
            key={fieldId}
            id={fieldId}
            type="text"
            className={styles.OtpInput}
            maxLength={1}
            value={otp[index]}
            onChange={(e) => handleOtpChange(e, index)}
            onKeyDown={(e) => handleOtpKeyDown(e, index)}
            onFocus={(e) => e.target.select()}
            onPaste={handlePaste}
          />
        ))}
      </div>

      {validationStep2.errors.code && validationStep2.touched.code && (
        <span className={styles.ErrorText}>
          {validationStep2.errors.code}
        </span>
      )}

      <p className={styles.OtpTimer}>
        Chưa nhận được mã OTP?
        {' '}
        {seconds > 0 ? (
          <span className={styles.TimerHighlight}>
            {seconds}
            s
          </span>
        ) : (
          <button
            type="button"
            className={styles.ResendLink}
            onClick={handleResendOtp}
          >
            Gửi lại
          </button>
        )}
      </p>

      <AppButton
        classes={styles.SubmitBtn}
        loading={forgotPasswordLoading}
        type="submit"
        disabled={validationStep2.values.code.length < 6}
      >
        XÁC NHẬN
      </AppButton>
    </form>
  );

  if (loginType === LOGIN_TYPE.EXTRANET) {
    return (
      <div className={styles.Container}>
        <Meta name="Quên mật khẩu" />

        <header className={styles.Header}>
          <div className={styles.HeaderContent}>
            <img
              src={images.logoBookeseFull.default}
              alt="logo-bookese"
              className={styles.HeaderLogo}
            />
          </div>
        </header>

        <main className={styles.Main}>
          <div className={styles.Card}>
            <div className={styles.CardHeader}>
              <h1 className={styles.CardTitle}>Nhập mã OTP</h1>
              <p className={styles.CardDescription}>
                Chúng tôi đã gửi mã OTP đến Email Bạn
              </p>
            </div>
            <div className={styles.CardBody}>
              {renderOtpForm()}
            </div>
          </div>
        </main>

        <footer className={styles.Footer}>
          <p className={styles.Copyright}>
            © 2022 Bookese. Design and Develop by Wiicamp
          </p>
        </footer>
      </div>
    );
  }

  // Intranet fallback - như cũ
  return (
    <>
      <Meta name="Quên mật khẩu" />

      <Authentication
        title="Xin chào!"
        description="Đăng nhập để đến quản lý chỗ nghỉ"
      >
        <form
          onSubmit={(e) => { e.preventDefault(); validationStep2.handleSubmit(); }}
          className={styles.Page}
        >
          <div className="Login__Email">
            <Label className="Login__Label">
              Nhập mã OTP
            </Label>
            <input
              type="text"
              className={`phone-input ${validationStep2.values.code.length > 5 && styles.hiddenCaret}`}
              name="code"
              id="code"
              maxLength="6"
              value={validationStep2.values.code}
              onChange={validationStep2.handleChange}
              onBlur={validationStep2.handleBlur}
            />
          </div>

          <div className="Login__Forgot-Password">
            Chưa nhận được mã OTP? 30s
          </div>

          <AppButton
            classes="Login__Btn-Login"
            loading={forgotPasswordLoading}
            type="submit"
          >
            KẾ TIẾP
          </AppButton>
        </form>
      </Authentication>
    </>
  );
};

ConfirmPassword.propTypes = {
  loginType: PropTypes.string.isRequired,
  forgotPasswordLoading: PropTypes.bool.isRequired,
  onSubmitOtp: PropTypes.func.isRequired,
  onResendOtp: PropTypes.func,
};

ConfirmPassword.defaultProps = {
  onResendOtp: () => {},
};

export default ConfirmPassword;
